import React, { useEffect, useState } from 'react';
import { Heart, Star, Crown, Gift, Award, Sparkles } from 'lucide-react';

const HeroSection = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    
    const handleMouseMove = (e) => {
      setMousePosition({
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="acasa" className="relative min-h-screen flex items-center justify-center overflow-hidden cream-pattern">
      {/* Background Gradient Animat */}
      <div className="absolute inset-0 animated-gradient opacity-80"></div>
      
      {/* Overlay pentru contrast */}
      <div className="absolute inset-0 bg-cream-50/40 backdrop-blur-sm"></div>

      {/* Particle System */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-2 h-2 rounded-full opacity-30 ${
              i % 3 === 0 ? 'bg-navy-300' : i % 3 === 1 ? 'bg-gold-300' : 'bg-emerald-300'
            } ${i % 2 === 0 ? 'particle-1' : 'particle-2'}`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${12 + Math.random() * 6}s`
            }}
          />
        ))}
      </div>

      {/* Floating Icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <Heart 
          className="absolute top-20 left-10 text-emerald-300/20 w-8 h-8 float" 
          style={{ animationDelay: '0s' }} 
        />
        <Star 
          className="absolute top-32 right-16 text-gold-300/20 w-6 h-6 float-delayed" 
          style={{ animationDelay: '2s' }} 
        />
        <Crown 
          className="absolute bottom-32 left-20 text-navy-300/20 w-10 h-10 float" 
          style={{ animationDelay: '4s' }} 
        />
        <Gift 
          className="absolute bottom-20 right-10 text-emerald-300/20 w-7 h-7 float-delayed" 
          style={{ animationDelay: '6s' }} 
        />
        <Award 
          className="absolute top-1/2 left-8 text-gold-300/15 w-8 h-8 float" 
          style={{ animationDelay: '8s' }} 
        />
      </div>

      {/* Parallax Background Elements */}
      <div 
        className="absolute inset-0"
        style={{
          transform: `translate(${mousePosition.x * 15}px, ${mousePosition.y * 15}px)`,
          transition: 'transform 0.5s ease-out'
        }}
      >
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-gradient-to-r from-navy-200/10 to-gold-200/10 blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-gradient-to-r from-emerald-200/10 to-navy-200/10 blur-3xl"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className={`transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {/* Tagline */}
          <div className="fade-in-up mb-8">
            <span className="inline-flex items-center px-8 py-3 rounded-full glass border border-navy-200/40 text-navy-700 font-medium text-sm shadow-lg">
              <Crown className="w-4 h-4 mr-2 text-gold-600 animate-pulse" />
              Experți în Organizarea de Botezuri de Neuitat
            </span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-6xl lg:text-8xl font-bold mb-8 leading-tight font-display">
            <span className="fade-in-up stagger-1 block text-navy-800 drop-shadow-lg">
              Botez de
            </span>
            <span className="fade-in-up stagger-2 block">
              <span className="elegant-glow text-navy-900 relative">
                Poveste
                <div className="absolute -inset-4 bg-gradient-to-r from-gold-200/20 to-navy-200/20 blur-xl -z-10 rounded-xl"></div>
              </span>
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl lg:text-3xl text-gray-700 mb-12 max-w-5xl mx-auto leading-relaxed fade-in-up stagger-3 drop-shadow-sm font-light">
            Transformăm ziua botezului copilului dumneavoastră într-o 
            <span className="gradient-gold font-semibold"> amintire de neuitat</span>, 
            cu atenție la detalii și dragoste în fiecare element.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16 fade-in-up stagger-4">
            <button 
              onClick={() => scrollToSection('contact')}
              className="morph-button relative px-10 py-4 text-lg font-bold text-white bg-gradient-to-r from-navy-700 to-navy-800 rounded-full shadow-navy hover:shadow-2xl hover-lift transition-all duration-300 overflow-hidden group min-w-[220px]"
            >
              <span className="relative z-10 flex items-center justify-center">
                <Heart className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
                Contactează-ne
              </span>
            </button>
            
            <button 
              onClick={() => scrollToSection('servicii')}
              className="morph-button relative px-10 py-4 text-lg font-bold text-navy-700 border-2 border-navy-600/60 backdrop-blur-sm rounded-full hover:bg-navy-600 hover:text-white hover-lift transition-all duration-300 group min-w-[220px]"
            >
              <span className="relative z-10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 mr-2 group-hover:rotate-180 transition-transform duration-500" />
                Vezi Serviciile
              </span>
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto fade-in-up stagger-5">
            {[
              { number: "150+", label: "Botezuri organizate", icon: Crown, gradient: "from-navy-500 to-navy-700" },
              { number: "5★", label: "Evaluare medie", icon: Star, gradient: "from-gold-500 to-gold-700" },
              { number: "24h", label: "Timp de răspuns", icon: Heart, gradient: "from-emerald-500 to-emerald-700" }
            ].map((stat, index) => (
              <div key={index} className={`text-center fade-in-scale stagger-${index + 1}`}>
                <div className="glass rounded-2xl p-8 hover-lift group border border-white/30 shadow-lg">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${stat.gradient} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                    <stat.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-4xl font-bold text-navy-800 mb-2 group-hover:text-gold-600 transition-colors duration-300">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 fade-in-up stagger-6">
        <div className="flex flex-col items-center text-navy-600/70">
          <span className="text-sm font-medium mb-3">Descoperă mai multe</span>
          <div className="w-6 h-10 border-2 border-navy-400/40 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-navy-400/60 rounded-full animate-pulse mt-2"></div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      {/* <div className="absolute top-10 left-10 w-20 h-20 border border-navy-200/20 rounded-full rotating-border"></div>
      <div className="absolute bottom-10 right-10 w-32 h-32 border border-gold-200/20 rounded-full rotating-border" style={{ animationDelay: '2s' }}></div> */}
      
      {/* Gradient Overlays pentru depth */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-cream-100/30 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white/20 to-transparent"></div>
    </section>
  );
};

export default HeroSection;